import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, TextField, Button, Alert, Tabs, Tab,
} from '@mui/material';
import { loginWithMagicLink, loginWithPassword, clearError } from '../store/authSlice.js';
import type { RootState, AppDispatch } from '../store/index.js';
import { colors } from '../styles/theme.js';

export default function LoginPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    const result = await dispatch(loginWithMagicLink(email));
    if (loginWithMagicLink.fulfilled.match(result)) {
      setMagicLinkSent(true);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    const result = await dispatch(loginWithPassword({ email, password }));
    if (loginWithPassword.fulfilled.match(result)) {
      navigate('/scan');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.black,
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          p: 5,
          bgcolor: colors.white,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            <Box component="span" sx={{ color: colors.lime, bgcolor: colors.black, px: 1, py: 0.5, borderRadius: 1 }}>
              Tracking
            </Box>{' '}
            Scanner
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.black60, fontFamily: "'Amiri', serif", fontStyle: 'italic', mt: 1 }}
          >
            ambition by mso digital
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setMagicLinkSent(false); dispatch(clearError()); }}
          sx={{ mb: 3 }}
          variant="fullWidth"
        >
          <Tab label="Passwort" />
          <Tab label="Magic Link" />
        </Tabs>

        {tab === 0 && (
          <Box component="form" onSubmit={handlePasswordLogin}>
            <TextField
              fullWidth
              label="E-Mail-Adresse"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Passwort"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !email || !password}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Anmelden...' : 'Anmelden'}
            </Button>
          </Box>
        )}

        {tab === 1 && (
          magicLinkSent ? (
            <Alert severity="success">
              Falls ein Account existiert, wurde ein Login-Link an <strong>{email}</strong> gesendet.
              Bitte prüfe dein Postfach.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleMagicLink}>
              <TextField
                fullWidth
                label="E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading || !email}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Wird gesendet...' : 'Login-Link senden'}
              </Button>
            </Box>
          )
        )}
      </Paper>
    </Box>
  );
}
